import React from "react";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { createV3ClientAssessmentByType } from "api/assessments";
import { uploadCustomerDocumentAsync } from "api/customers";
import { GET_V3_CLIENT_ASSESSMENTS } from "@constants/reactQueryKeys";
import { postUploadFiles } from "hooks/postUploadFiles";
import { useGlobalToast } from "components/GlobalToastProvider";
import * as selectors from "selectors";
import { space3 } from "../styled";
import { aflsStepTotal, getSrs2Severity, getSrs2TotalSeverity } from "../utils";
import {
  getPeakAgeRange,
  getTypicalAgeScore,
  getFoundationalLearningAgeRange,
  getPerceptualLearningAgeRange,
  getVerbalComprehensionAgeRange,
  getVerbalReasoningMemoryAgeRange,
  getFoundationalLearningBasicSocialAgeRange,
  getAdvancedVerbalComprehensionAdvancedMathAgeRange,
  getVerbalReasoningAdvancedReadingWritingAgeRange,
  getDirectTeachingAgeRange,
  getGeneralizationAgeRange,
  getDifferenceInScore,
  sumScores,
  peakAgeRangeToEnum,
} from "../util/peakCalculations";
import type {
  AflsFormData,
  AssessmentRecord,
  AssessmentType,
  PeakFormData,
  Srs2FormData,
  VbmappMilestonesFormData,
} from "../types";
import VbmappMilestonesDataEntry from "./DataEntry/VbmappMilestonesDataEntry";
import AflsDataEntry from "./DataEntry/AflsDataEntry";
import Srs2DataEntry from "./DataEntry/Srs2DataEntry";
import PeakDataEntry from "./DataEntry/PeakDataEntry";

interface DataEntryDialogContentProps {
  clientId?: string;
  assessmentType: AssessmentType;
  currentUserName: string;
  clientAgeInYears: number | null;
  mostRecentAssessment: AssessmentRecord | null;
  onClose: () => void;
}

const DATA_ENTRY_SUPPORTED: AssessmentType[] = ["vbmappMilestones", "afls", "srs2", "peak"];

const PEAK_DT_MODULE_TRIANGLE = "PEAK_DT_MODULE_TRIANGLE";
const PEAK_G_MODULE_TRIANGLE = "PEAK_G_MODULE_TRIANGLE";
const PEAK_E_MODULE_TRIANGLE = "PEAK_E_MODULE_TRIANGLE";
const PEAK_T_MODULE_TRIANGLE = "PEAK_T_MODULE_TRIANGLE";
const VB_MAPP_MILESTONES_GRID = "VB_MAPP_MILESTONES_GRID";
const AFLS_SCORING_GRID = "AFLS_SCORING_GRID";

/**
 * Uploads a single assessment file to S3 and registers it in the document library.
 * Returns the new document's integer ID, or null if the upload or registration fails.
 */
async function uploadAssessmentFile(
  file: File,
  cognitoId: string,
  clientId: string,
  userId: number,
  documentType: string
): Promise<number | null> {
  try {
    const { success: s3Key, errors } = await postUploadFiles(cognitoId, [{ file }]);
    if (errors || !s3Key) return null;

    const response = await uploadCustomerDocumentAsync({
      filename: s3Key.split("/").slice(1).join("/"),
      key: s3Key,
      display_filename: file.name,
      clientId,
      type: documentType,
      uploaded_by: userId,
      file_size: file.size,
    });

    const documentId = response?.data?.document_id;
    if (documentId == null) return null;

    const numericDocumentId = Number(documentId);
    return Number.isFinite(numericDocumentId) ? numericDocumentId : null;
  } catch {
    return null;
  }
}

const VBMAPP_SCORE_KEYS: Array<
  keyof Omit<VbmappMilestonesFormData, "administrationDate" | "uploadedFiles">
> = [
  "mand",
  "tact",
  "listenerResponding",
  "visualPerceptualMts",
  "play",
  "social",
  "motorImitation",
  "echoic",
  "vocal",
  "listenerRespondingLrffc",
  "intraverbal",
  "group",
  "linguistics",
  "reading",
  "writing",
  "math",
];

function getVbmappMilestonesPayload(
  form: VbmappMilestonesFormData,
  _administratorName: string,
  milestonesGridDocumentId: number | null
) {
  const { uploadedFiles: _uploadedFiles, administratorName, ...fields } = form;
  // Compute total_score (schema field) from entered sub-scores.
  const entered = VBMAPP_SCORE_KEYS.map((k) => fields[k]).filter((v): v is number => v !== null);
  const totalScore = entered.length > 0 ? entered.reduce((a, b) => a + b, 0) : null;
  return { ...fields, totalScore, administratorName, milestonesGridDocumentId };
}

function getAflsPayload(form: AflsFormData, scoringGridDocumentId: number | null) {
  const [
    basicSkillsTotal,
    homeSkillsTotal,
    schoolSkillsTotal,
    communitySkillsTotal,
    independentLivingTotal,
    vocationalSkillsTotal,
  ] = form.steps.map(aflsStepTotal);

  // Collect individual sub-skill scores for all completed steps.
  const subSkillScores: Record<string, number | null> = {};
  form.steps.forEach((step) => {
    if (step.stepCompleted) {
      step.skills.forEach((skill) => {
        if (skill.apiField) {
          subSkillScores[skill.apiField] = skill.score;
        }
      });
    }
  });

  return {
    administrationDate: form.administrationDate,
    administratorName: form.administratorName,
    basicSkillsTotal,
    homeSkillsTotal,
    schoolSkillsTotal,
    communitySkillsTotal,
    independentLivingTotal,
    vocationalSkillsTotal,
    aflsScoringGridDocumentId: scoringGridDocumentId,
    ...subSkillScores,
  };
}

/**
 * Builds the SRS-2 payload including derived severity/level fields that the
 * schema accepts from the caller (present in srs2_assessment table).
 */
function getSrs2Payload(form: Srs2FormData) {
  // totalTScore is derived by the backend (sum of 6 subscales). Sending a
  // caller-supplied value causes a 400 if it doesn't match; exclude it here.
  const { totalTScore: _totalTScore, administratorName, ...formWithoutTotal } = form;
  return {
    ...formWithoutTotal,
    administratorName,
    // Severity/level fields — present in schema (srs2_assessment), derived from T-scores.
    severityRange: getSrs2TotalSeverity(form.totalTScore),
    awarenessLevel: getSrs2Severity(form.awarenessTScore),
    cognitionLevel: getSrs2Severity(form.cognitionTScore),
    communicationLevel: getSrs2Severity(form.communicationTScore),
    motivationLevel: getSrs2Severity(form.motivationTScore),
    srsSciLevel: getSrs2Severity(form.srsSciTScore),
    rirbLevel: getSrs2Severity(form.rirbTScore),
  };
}

/**
 * Builds the PEAK payload including all derived values stored in peak_assessment:
 *   - Module totals (dt/g/e/t_total_score) — server also computes these but accepts caller values.
 *   - Typical age scores — NOT server-computed, must come from caller.
 *   - Difference-in-score fields — NOT server-computed, must come from caller.
 *   - Age range equivalents — accepted from caller (thresholds from PEAK book).
 */
function getPeakPayload(
  form: PeakFormData,
  clientAgeInYears: number | null,
  documentIds: {
    dtModuleTriangleDocumentId: number | null;
    gModuleTriangleDocumentId: number | null;
    eModuleTriangleDocumentId: number | null;
    tModuleTriangleDocumentId: number | null;
  }
) {
  const ageRange = getPeakAgeRange(clientAgeInYears);

  // ── Direct Teaching (DT) subscale derived values ──────────────────────────
  const dtFoundationalLearningTypicalAgeScore = getTypicalAgeScore(
    "foundationalLearning",
    ageRange
  );
  const dtFoundationalLearningAgeRangeEquivalent = peakAgeRangeToEnum(
    getFoundationalLearningAgeRange(form.foundationalLearning)
  );
  const dtFoundationalLearningDifferenceInScore = getDifferenceInScore(
    dtFoundationalLearningTypicalAgeScore,
    form.foundationalLearning
  );

  const dtPerceptualLearningTypicalAgeScore = getTypicalAgeScore("perceptualLearning", ageRange);
  const dtPerceptualLearningAgeRangeEquivalent = peakAgeRangeToEnum(
    getPerceptualLearningAgeRange(form.perceptualLearning)
  );
  const dtPerceptualLearningDifferenceInScore = getDifferenceInScore(
    dtPerceptualLearningTypicalAgeScore,
    form.perceptualLearning
  );

  const dtVerbalComprehensionTypicalAgeScore = getTypicalAgeScore("verbalComprehension", ageRange);
  const dtVerbalComprehensionAgeRangeEquivalent = peakAgeRangeToEnum(
    getVerbalComprehensionAgeRange(form.verbalComprehension)
  );
  const dtVerbalComprehensionDifferenceInScore = getDifferenceInScore(
    dtVerbalComprehensionTypicalAgeScore,
    form.verbalComprehension
  );

  const dtVerbalReasoningMemoryTypicalAgeScore = getTypicalAgeScore(
    "verbalReasoningMemory",
    ageRange
  );
  const dtVerbalReasoningMemoryAgeRangeEquivalent = peakAgeRangeToEnum(
    getVerbalReasoningMemoryAgeRange(form.verbalReasoningMemory)
  );
  const dtVerbalReasoningMemoryDifferenceInScore = getDifferenceInScore(
    dtVerbalReasoningMemoryTypicalAgeScore,
    form.verbalReasoningMemory
  );

  // DT module total
  const dtTotalScore = sumScores([
    form.foundationalLearning,
    form.perceptualLearning,
    form.verbalComprehension,
    form.verbalReasoningMemory,
  ]);
  const dtTotalTypicalAgeScore = getTypicalAgeScore("directTeachingTotal", ageRange);
  const dtAgeRangeEquivalent = peakAgeRangeToEnum(getDirectTeachingAgeRange(dtTotalScore));
  const dtTotalDifferenceInScore = getDifferenceInScore(dtTotalTypicalAgeScore, dtTotalScore);

  // ── Generalization (G) subscale derived values ────────────────────────────
  const gFoundationalLearningBasicSocialTypicalAgeScore = getTypicalAgeScore(
    "foundationalLearningBasicSocialSkills",
    ageRange
  );
  const gFoundationalLearningBasicSocialAgeRangeEquivalent = peakAgeRangeToEnum(
    getFoundationalLearningBasicSocialAgeRange(form.foundationalLearningBasicSocialSkills)
  );
  const gFoundationalLearningBasicSocialDifferenceInScore = getDifferenceInScore(
    gFoundationalLearningBasicSocialTypicalAgeScore,
    form.foundationalLearningBasicSocialSkills
  );

  const gBasicVerbalComprehensionAdvancedSocialTypicalAgeScore = getTypicalAgeScore(
    "basicVerbalComprehensionAdvancedSocialSkills",
    ageRange
  );
  // No age range equivalent for this subscale per spec — schema field is nullable.
  const gBasicVerbalComprehensionAdvancedSocialAgeRangeEquivalent = null;
  const gBasicVerbalComprehensionAdvancedSocialDifferenceInScore = getDifferenceInScore(
    gBasicVerbalComprehensionAdvancedSocialTypicalAgeScore,
    form.basicVerbalComprehensionAdvancedSocialSkills
  );

  const gAdvancedVerbalProblemSolvingTypicalAgeScore = getTypicalAgeScore(
    "advancedVerbalComprehensionAdvancedMathSkills",
    ageRange
  );
  const gAdvancedVerbalProblemSolvingAgeRangeEquivalent = peakAgeRangeToEnum(
    getAdvancedVerbalComprehensionAdvancedMathAgeRange(
      form.advancedVerbalComprehensionAdvancedMathSkills
    )
  );
  const gAdvancedVerbalProblemSolvingDifferenceInScore = getDifferenceInScore(
    gAdvancedVerbalProblemSolvingTypicalAgeScore,
    form.advancedVerbalComprehensionAdvancedMathSkills
  );

  const gVerbalReasoningAdvancedTypicalAgeScore = getTypicalAgeScore(
    "verbalReasoningAdvancedReadingWritingSkills",
    ageRange
  );
  const gVerbalReasoningAdvancedAgeRangeEquivalent = peakAgeRangeToEnum(
    getVerbalReasoningAdvancedReadingWritingAgeRange(
      form.verbalReasoningAdvancedReadingWritingSkills
    )
  );
  const gVerbalReasoningAdvancedDifferenceInScore = getDifferenceInScore(
    gVerbalReasoningAdvancedTypicalAgeScore,
    form.verbalReasoningAdvancedReadingWritingSkills
  );

  // G module total
  const gTotalScore = sumScores([
    form.foundationalLearningBasicSocialSkills,
    form.basicVerbalComprehensionAdvancedSocialSkills,
    form.advancedVerbalComprehensionAdvancedMathSkills,
    form.verbalReasoningAdvancedReadingWritingSkills,
  ]);
  const gTotalTypicalAgeScore = getTypicalAgeScore("generalizationTotal", ageRange);
  const gAgeRangeEquivalent = peakAgeRangeToEnum(getGeneralizationAgeRange(gTotalScore));
  const gTotalDifferenceInScore = getDifferenceInScore(gTotalTypicalAgeScore, gTotalScore);

  // ── Equivalence (E) module total ──────────────────────────────────────────
  const eTotalScore = sumScores([
    form.reflexivity,
    form.symmetry,
    form.transitivity,
    form.equivalence,
  ]);

  // ── Transformation (T) module total ──────────────────────────────────────
  const tTotalScore = sumScores([
    form.nonArbitraryLevel1,
    form.culturalLevel2,
    form.arbitraryLevel3,
    form.complexLevel4,
  ]);

  return {
    administrationDate: form.administrationDate,
    // DT subscale scores
    dtFoundationalLearningScore: form.foundationalLearning,
    dtPerceptualLearningScore: form.perceptualLearning,
    dtVerbalComprehensionScore: form.verbalComprehension,
    dtVerbalReasoningMemoryScore: form.verbalReasoningMemory,
    // GEN subscale scores
    gFoundationalLearningBasicSocialScore: form.foundationalLearningBasicSocialSkills,
    gBasicVerbalComprehensionAdvancedSocialScore: form.basicVerbalComprehensionAdvancedSocialSkills,
    gAdvancedVerbalComprehensionProblemSolvingScore:
      form.advancedVerbalComprehensionAdvancedMathSkills,
    gVerbalReasoningAdvancedProblemSolvingScore: form.verbalReasoningAdvancedReadingWritingSkills,
    // EQ subscale scores
    eReflexivityScore: form.reflexivity,
    eSymmetryScore: form.symmetry,
    eTransitivityScore: form.transitivity,
    eEquivalenceScore: form.equivalence,
    // T subscale scores
    tNonArbitraryLevel1Score: form.nonArbitraryLevel1,
    tCulturalLevel2Score: form.culturalLevel2,
    tArbitraryLevel3Score: form.arbitraryLevel3,
    tComplexLevel4Score: form.complexLevel4,
    administratorName: form.administratorName,
    // DT subscale derived
    dtFoundationalLearningTypicalAgeScore,
    dtFoundationalLearningAgeRangeEquivalent,
    dtFoundationalLearningDifferenceInScore,
    dtPerceptualLearningTypicalAgeScore,
    dtPerceptualLearningAgeRangeEquivalent,
    dtPerceptualLearningDifferenceInScore,
    dtVerbalComprehensionTypicalAgeScore,
    dtVerbalComprehensionAgeRangeEquivalent,
    dtVerbalComprehensionDifferenceInScore,
    dtVerbalReasoningMemoryTypicalAgeScore,
    dtVerbalReasoningMemoryAgeRangeEquivalent,
    dtVerbalReasoningMemoryDifferenceInScore,
    // DT module total
    dtTotalScore,
    dtTotalTypicalAgeScore,
    dtAgeRangeEquivalent,
    dtTotalDifferenceInScore,
    // G subscale derived
    gFoundationalLearningBasicSocialTypicalAgeScore,
    gFoundationalLearningBasicSocialAgeRangeEquivalent,
    gFoundationalLearningBasicSocialDifferenceInScore,
    gBasicVerbalComprehensionAdvancedSocialTypicalAgeScore,
    gBasicVerbalComprehensionAdvancedSocialAgeRangeEquivalent,
    gBasicVerbalComprehensionAdvancedSocialDifferenceInScore,
    gAdvancedVerbalProblemSolvingTypicalAgeScore,
    gAdvancedVerbalProblemSolvingAgeRangeEquivalent,
    gAdvancedVerbalProblemSolvingDifferenceInScore,
    gVerbalReasoningAdvancedTypicalAgeScore,
    gVerbalReasoningAdvancedAgeRangeEquivalent,
    gVerbalReasoningAdvancedDifferenceInScore,
    // G module total
    gTotalScore,
    gTotalTypicalAgeScore,
    gAgeRangeEquivalent,
    gTotalDifferenceInScore,
    // E module total
    eTotalScore,
    // T module total
    tTotalScore,
    // Module triangle document IDs
    ...documentIds,
  };
}

/**
 * Renders the correct data entry form for a given assessment type.
 * Intended to be hosted inside a Dialog. Supported: VB-MAPP Milestones, AFLS, SRS-2, PEAK.
 */
export default function DataEntryDialogContent({
  clientId,
  assessmentType,
  currentUserName,
  clientAgeInYears,
  mostRecentAssessment,
  onClose,
}: DataEntryDialogContentProps) {
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const customerDetails = useSelector(selectors.getCustomerDetails);
  const currentUser = useSelector(selectors.getUser);

  // Guards against re-entrant/concurrent AFLS saves (the Save button is also
  // disabled while this is true). Prevents duplicate assessment rows and
  // duplicate document uploads from repeated clicks.
  const [isSavingAfls, setIsSavingAfls] = React.useState(false);
  // Caches the uploaded scoring-grid document id per file so a retry reuses the
  // same document instead of uploading a new one each attempt.
  const aflsGridUploadRef = React.useRef<{ file: File; id: number | null } | null>(null);

  const { mutate: createAssessment, mutateAsync: createAssessmentAsync } = useMutation(
    ({ type, payload }: { type: AssessmentType; payload: Record<string, unknown> }) => {
      if (!clientId) {
        return Promise.reject(new Error("Missing client ID for assessment creation"));
      }
      return createV3ClientAssessmentByType(clientId, type, payload);
    },
    {
      onSuccess: () => {
        if (clientId) {
          queryClient.invalidateQueries([GET_V3_CLIENT_ASSESSMENTS, clientId]);
        }
        onClose();
      },
      // Requirement: handle API errors quietly (no UI error surface).
      onError: () => {},
    }
  );

  const handleSaveVbmappMilestones = async (form: VbmappMilestonesFormData) => {
    let milestonesGridDocumentId: number | null = null;
    const cognitoId: string | undefined = customerDetails?.details?.cognito_id;
    const userId: number | undefined = currentUser?.id;

    if (form.uploadedFiles[0] && !form.uploadedFiles[0].errors && cognitoId && clientId && userId) {
      milestonesGridDocumentId = await uploadAssessmentFile(
        form.uploadedFiles[0].file,
        cognitoId,
        clientId,
        userId,
        VB_MAPP_MILESTONES_GRID
      );
    }

    createAssessment({
      type: "vbmappMilestones",
      payload: getVbmappMilestonesPayload(form, currentUserName, milestonesGridDocumentId),
    });
  };

  const handleSaveAfls = async (form: AflsFormData) => {
    // Block re-entrant/concurrent submits (also guarded via the disabled button).
    if (isSavingAfls) return;

    const cognitoId: string | undefined = customerDetails?.details?.cognito_id;
    const userId: number | undefined = currentUser?.id;

    setIsSavingAfls(true);
    try {
      let scoringGridDocumentId: number | null = null;
      // Pick the most recently selected *accepted* scoring-grid file, skipping
      // any rejected uploads (errors) — the uploader appends wrappers, so the
      // first entry may be an old or invalid file.
      const validUploads = form.uploadedFiles.filter((f) => !f.errors);
      const file = validUploads[validUploads.length - 1]?.file;

      if (file && cognitoId && clientId && userId) {
        // Upload the scoring grid once and reuse its document id across retries,
        // so a failed save doesn't create a new document row every attempt.
        if (aflsGridUploadRef.current?.file === file && aflsGridUploadRef.current.id != null) {
          scoringGridDocumentId = aflsGridUploadRef.current.id;
        } else {
          scoringGridDocumentId = await uploadAssessmentFile(
            file,
            cognitoId,
            clientId,
            userId,
            AFLS_SCORING_GRID
          );
          aflsGridUploadRef.current = { file, id: scoringGridDocumentId };
        }
      }

      // Awaited so failures surface (below) instead of being swallowed silently.
      await createAssessmentAsync({
        type: "afls",
        payload: getAflsPayload(form, scoringGridDocumentId),
      });
      // On success the mutation's onSuccess closes the dialog (unmounts).
    } catch {
      showToast({
        message: "Whoops! Something went wrong saving the assessment. Please try again.",
        errorState: true,
      });
      setIsSavingAfls(false);
    }
  };

  const handleSaveSrs2 = (form: Srs2FormData) => {
    createAssessment({ type: "srs2", payload: getSrs2Payload(form) });
  };

  const handleSavePeak = async (form: PeakFormData) => {
    const cognitoId: string | undefined = customerDetails?.details?.cognito_id;
    const userId: number | undefined = currentUser?.id;

    function hasFileErrors(file: { file: File; errors: boolean } | undefined): boolean {
      return Boolean(file?.errors);
    }

    async function uploadIfAvailable(
      file: { file: File; errors: boolean } | undefined,
      documentType: string
    ): Promise<number | null> {
      if (!file || file.errors || !cognitoId || !clientId || !userId) return null;
      return uploadAssessmentFile(file.file, cognitoId, clientId, userId, documentType);
    }

    const peakUploads = [
      form.uploadedFilesDt[0],
      form.uploadedFilesGen[0],
      form.uploadedFilesEq[0],
      form.uploadedFilesTr[0],
    ];

    if (peakUploads.some(hasFileErrors)) return;

    const [dtResult, gResult, eResult, tResult] = await Promise.allSettled([
      uploadIfAvailable(form.uploadedFilesDt[0], PEAK_DT_MODULE_TRIANGLE),
      uploadIfAvailable(form.uploadedFilesGen[0], PEAK_G_MODULE_TRIANGLE),
      uploadIfAvailable(form.uploadedFilesEq[0], PEAK_E_MODULE_TRIANGLE),
      uploadIfAvailable(form.uploadedFilesTr[0], PEAK_T_MODULE_TRIANGLE),
    ]);

    createAssessment({
      type: "peak",
      payload: getPeakPayload(form, clientAgeInYears, {
        dtModuleTriangleDocumentId: dtResult.status === "fulfilled" ? dtResult.value : null,
        gModuleTriangleDocumentId: gResult.status === "fulfilled" ? gResult.value : null,
        eModuleTriangleDocumentId: eResult.status === "fulfilled" ? eResult.value : null,
        tModuleTriangleDocumentId: tResult.status === "fulfilled" ? tResult.value : null,
      }),
    });
  };

  const isSupported = DATA_ENTRY_SUPPORTED.includes(assessmentType);

  if (!isSupported) {
    return (
      <Stack rowGap={space3}>
        <Typography variant="body2" color="text.secondary">
          Manual data entry is not yet available for this assessment type.
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      {assessmentType === "vbmappMilestones" && (
        <VbmappMilestonesDataEntry
          administratorName={currentUserName}
          previousAssessment={mostRecentAssessment}
          onCancel={onClose}
          onSave={handleSaveVbmappMilestones}
        />
      )}

      {assessmentType === "afls" && (
        <AflsDataEntry
          administratorName={currentUserName}
          previousAssessment={mostRecentAssessment}
          onCancel={onClose}
          onSave={handleSaveAfls}
          saving={isSavingAfls}
        />
      )}

      {assessmentType === "srs2" && (
        <Srs2DataEntry
          administratorName={currentUserName}
          onCancel={onClose}
          onSave={handleSaveSrs2}
        />
      )}

      {assessmentType === "peak" && (
        <PeakDataEntry
          administratorName={currentUserName}
          clientAgeInYears={clientAgeInYears}
          previousAssessment={mostRecentAssessment}
          onCancel={onClose}
          onSave={handleSavePeak}
        />
      )}
    </>
  );
}
